import React from 'react';
import { useDispatch } from 'react-redux';

import { NotificationType } from 'modules/notifications/types/Notification';

import { isAuthorizedFile } from 'utils/formUtils';

import { MediaSupport } from 'types/mediaTypes';

import { addNotification } from 'modules/notifications/actions';

export interface UseBrowseProps {
  limit?: number;
  /** @description This is only used for the error translation  */
  absoluteLimit?: number;
}

interface BrowseParams {
  accept?: MediaSupport;
  multiple?: boolean;
  capture?: string;
}

type UseBrowse = (props?: UseBrowseProps) => (params?: BrowseParams) => Promise<FileList | Array<File>>;

export const unMount = (element: HTMLElement) => {
  const parent = element instanceof HTMLElement && element.parentNode;
  if (parent) {
    return parent.removeChild(element);
  }
  return undefined;
};

const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent);

const useBrowse: UseBrowse = ({ limit, absoluteLimit } = {}) => {
  const dispatch = useDispatch();

  return React.useCallback(({ accept, multiple, capture } = {}) => new Promise((resolve, reject) => {
    const fileInput = document.createElement('input');

    fileInput.type = 'file';
    fileInput.dataset.testid = 'hidden-file-input';
    fileInput.style.display = 'none';

    fileInput.accept = accept ? [...(accept.extensions ?? []), ...accept.types ?? []].join(',') : 'image/*';
    fileInput.multiple = multiple;
    if (capture) {
      fileInput.capture = capture;
    }

    fileInput.addEventListener('change', () => {
      let files = Array.from(fileInput.files);

      if (typeof limit === 'number' && files.length > limit) {
        dispatch(addNotification({
          title: {
            id: 'media.errorTitle',
            defaultMessage: 'Error',
          },
          description: {
            id: 'media.fileLimitReached',
            defaultMessage: 'You have exceeded the maximum number of files to import (Limit: {limit}).',
            values: { limit: absoluteLimit ?? limit },
          },
          type: NotificationType.ERROR,
        }));

        files = files.slice(0, limit);
      }

      const filteredFiles = files.filter((file) => isAuthorizedFile(file, accept));
      if (filteredFiles.length !== files.length) {
        dispatch(addNotification({
          title: {
            id: 'media.errorTitle',
            defaultMessage: 'Error',
          },
          description: {
            id: 'media.errorMessage',
            defaultMessage: 'This type of file is not allowed',
          },
          type: NotificationType.ERROR,
        }));
      }

      resolve(files);
      unMount(fileInput);
    }, { once: true });

    // Detect when the file browser is closed (i.e. cancelled if `onchange` didn't fire) using focus on window
    window.addEventListener('focus', () => {
      // setTimeout is needed to avoid race condition with `change` event (no less than 300ms)
      setTimeout(() => {
        if (fileInput.files.length === 0) {
          unMount(fileInput);
          reject();
        }
      }, isIOSSafari ? 3000 : 300);
    }, { once: true });

    document.body.appendChild(fileInput);
    fileInput.click();
  }), [absoluteLimit, dispatch, limit]);
};

export default useBrowse;
